import React, { forwardRef } from 'react';
import { cn } from '../../../utils/cn';

export type SelectSize = 'sm' | 'md' | 'lg';

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: SelectSize;
  label?: string;
  helperText?: string;
  errorText?: string;
  options: Array<{ value: string; label: string; style?: React.CSSProperties }>;
  placeholder?: string;
  fullWidth?: boolean;
  required?: boolean;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      size = 'md',
      label,
      helperText,
      errorText,
      options,
      placeholder,
      fullWidth = false,
      required = false,
      className,
      id,
      onFocus,
      onBlur,
      style,
      ...props
    },
    ref
  ) => {
    const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
    const hasError = !!errorText;

    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-3 py-2 text-sm',
      lg: 'px-4 py-3 text-base',
    };

    return (
      <div className={cn('relative', fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium mb-1"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {label}
            {required && (
              <span className="ml-1" style={{ color: 'var(--color-error-500)' }}>
                *
              </span>
            )}
          </label>
        )}

        <select
          ref={ref}
          id={selectId}
          className={cn(
            'w-full rounded-lg border transition-colors focus:outline-none focus:ring-1',
            sizeClasses[size],
            className
          )}
          style={{
            backgroundColor: 'var(--color-background-paper)',
            borderColor: hasError
              ? 'var(--color-error-500)'
              : 'var(--color-border-light)',
            color: 'var(--color-text-primary)',
            '--tw-ring-color': hasError
              ? 'var(--color-error-500)'
              : 'var(--color-primary-500)',
            ...style,
          } as React.CSSProperties}
          onFocus={(e) => {
            if (!hasError) {
              e.target.style.borderColor = 'var(--color-primary-500)';
            }
            onFocus?.(e);
          }}
          onBlur={(e) => {
            if (!hasError) {
              e.target.style.borderColor = 'var(--color-border-light)';
            }
            onBlur?.(e);
          }}
          aria-invalid={hasError}
          aria-describedby={
            hasError && errorText
              ? `${selectId}-error`
              : helperText
                ? `${selectId}-helper`
                : undefined
          }
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value} style={option.style}>
              {option.label}
            </option>
          ))}
        </select>

        {hasError && errorText && (
          <p id={`${selectId}-error`} className="mt-1 text-xs" style={{ color: 'var(--color-error-500)' }}>
            {errorText}
          </p>
        )}

        {!hasError && helperText && (
          <p id={`${selectId}-helper`} className="mt-1 text-xs opacity-70" style={{ color: 'var(--color-text-secondary)' }}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export { Select };

